import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { rem } from 'polished';
import { Link } from 'react-router-dom';
import {
  HeadingText,
  Alert,
  extraSmall,
  Button,
  ButtonSize,
} from '@unmind/design-system';
import RoutePath from '../App/RoutePath';
import { tracking } from '../App/Tracking';
import LoadingIndicator from '../Shared/LoadingIndicator';
import { useHelpResourcesFolders } from './hooks/useHelpResourcesFolders';

const ResourcesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(42)};
  padding: ${rem(40)};
  border: 1px solid ${({ theme }) => theme.colors.border.secondary};
  border-radius: ${rem(20)};
  width: 100%;
  margin-bottom: ${rem(56)};
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const ResourcesTitle = styled(HeadingText).attrs(({ theme }) => ({
  level: 2,
  sizes: [theme.typography.fontSizes.fontSize18],
  weight: theme.typography.fontWeights.semiBold,
}))`` as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

const ResourceItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  gap: ${rem(40)};

  ${extraSmall(css`
    flex-direction: row;
  `)}
`;

const AlertContainer = styled.div`
  max-width: ${rem(680)};
  align-self: center;
`;

const RetryOnErrorButton = styled(Button).attrs(() => ({
  size: ButtonSize.small,
}))`
  margin-left: ${rem(12)};
  background: ${({ theme }) => theme.colors.background.card};
  min-width: ${rem(96)};

  &:hover {
    background: ${({ theme }) => theme.colors.background.cardHover};
  }
`;

const trackResourceLinkClick = ({
  id,
  title,
  countryCode,
}: {
  id: string;
  title: string;
  countryCode: string;
}) => {
  tracking.track('help_landing_resource_clicked', {
    resourceId: id,
    resourceTitle: title,
    countryCode,
  });
};

const HelpResources = ({ countryCode }: { countryCode: string }) => {
  const { t: translate } = useTranslation('help');

  const { data, loading, error, refetch } = useHelpResourcesFolders();

  if (loading) {
    return <LoadingIndicator />;
  }

  const helpFolders = data?.helpContentResourceRoot || [];

  if (error || !helpFolders.length) {
    return (
      <ResourcesContainer>
        <ResourcesTitle>{translate('help_resources.title')}</ResourcesTitle>
        <AlertContainer>
          <Alert
            variant="error"
            title={translate('help_service_tabs.error_state.title')}
            body={translate('help_service_tabs.error_state.body')}
            size="small"
          >
            <RetryOnErrorButton
              label={translate(
                'help_service_tabs.error_state.refetch_button_label',
              )}
              onClick={async () => refetch()}
            />
          </Alert>
        </AlertContainer>
      </ResourcesContainer>
    );
  }

  return (
    <ResourcesContainer>
      <ResourcesTitle>{translate('help_resources.title')}</ResourcesTitle>
      <ResourceItemContainer>
        {helpFolders.map(({ title, id }) => (
          <Link
            key={id}
            to={`${RoutePath.Help}/${id}`}
            onClick={() => {
              trackResourceLinkClick({ id, title, countryCode });
            }}
          >
            {title}
          </Link>
        ))}
      </ResourceItemContainer>
    </ResourcesContainer>
  );
};

export default HelpResources;
